import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { defer, Observable, of, throwError } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { FeeRequest } from '../../dtos/request/fee-request';
import { BagsAndAncillariesDetail } from '../../dtos/response/bags-and-ancillaries-response/bags-and-ancillaries-detail';
import { BagsAndAncillariesResponse } from '../../dtos/response/bags-and-ancillaries-response/bags-and-ancillaries-response';
import { FeeDetail } from '../../dtos/response/fee-response/fee-detail';
import { FeeResponse } from '../../dtos/response/fee-response/fee-response';
import { timeoutError } from '../../models/timeout-error';
import { TimeoutLimit } from '../../models/timeout-limit';
import { GlobalEvent, GlobalEventService } from '../global-event-service/global-event.service';
import { DynamicFeesRequest } from '../../dtos/request/dynamic-fees/dynamic-fees-request';
import { DynamicFeesResponse } from '../../dtos/response/dynamic-fees/dynamic-fees-response';
import { DynamicFeeDetail } from '../../dtos/response/dynamic-fees/dynamic-fee-detail';

export interface FeeServiceAPI {
  getFees(request: FeeRequest, backgroundLookup: boolean): Observable<FeeResponse>;
}
/**
 * Injectable service to interface with fee endpoint in RAIN API
 */
@Injectable({
  providedIn: 'root',
})
export class FeeService implements FeeServiceAPI {
  private readonly http = inject(HttpClient);
  private readonly eventService = inject(GlobalEventService);

  getFees(request: FeeRequest, backgroundLookup: boolean = false): Observable<FeeResponse> {
    const options = {
      headers: new HttpHeaders({
        background: backgroundLookup ? 'true' : 'false',
      }),
    };
    return this.http
      .get<FeeDetail>(
        `api/fee/${request.confirmationCode}/${request.departureCity}/${request.passengerNameNumbers}/${request.flightDate}`,
        options
      )
      .pipe(
        timeout({
          each: TimeoutLimit.MEDIUM,
          with: () => defer(() => throwError(() => new HttpErrorResponse(timeoutError))),
        }),
        map((response) => ({ success: true, response })),
        catchError((err: HttpErrorResponse) => {
          if (timeoutError.statusText === err.statusText) {
            this.eventService.broadcastAjax(GlobalEvent.AJAX_END, err);
            return of({ success: false, error: 'Timeout' });
          } else if (err.status === 500) {
            return of({ success: false, error: 'System failure' });
          } else {
            return of({ success: false, error: 'Uncaught' });
          }
        })
      );
  }

  getAllBagsAndAncillaryFees(request: FeeRequest): Observable<BagsAndAncillariesResponse> {
    const options = {
      headers: new HttpHeaders({
        background: 'true',
      }),
    };
    return this.http
      .get<BagsAndAncillariesDetail>(
        `api/fee/bags-ancillaries/${request.confirmationCode}/${request.departureCity}/${request.passengerNameNumbers}/${request.flightDate}`,
        options
      )
      .pipe(
        timeout({
          each: TimeoutLimit.MEDIUM,
          with: () => defer(() => throwError(() => new HttpErrorResponse(timeoutError))),
        }),
        map((response) => ({ success: true, response })),
        catchError((err: HttpErrorResponse) => {
          const mappedError = err?.error?.TechnicalErrorMessage;
          if (timeoutError.statusText === err.statusText) {
            return of({ success: false, error: 'Timeout' });
          } else if (err.status === 500) {
            return of({ success: false, error: mappedError ?? 'System failure' });
          } else {
            return of({ success: false, error: mappedError ?? 'Uncaught' });
          }
        })
      );
  }

  getDynamicFees(request: DynamicFeesRequest, backgroundLookup: boolean = false): Observable<DynamicFeesResponse> {
    const options = {
      headers: new HttpHeaders({
        background: backgroundLookup ? 'true' : 'false',
      }),
    };

    return this.http.post<DynamicFeeDetail>(`api/fee/requested-fees`, request, options).pipe(
      timeout({
        each: TimeoutLimit.MEDIUM,
        with: () => defer(() => throwError(() => new HttpErrorResponse(timeoutError))),
      }),
      map((response) => ({ success: true, feeDetail: response })),
      catchError((err: HttpErrorResponse) => {
        if (timeoutError.status === err.status) {
          return of({ success: false, error: 'Timeout' });
        } else if (err.status === 500) {
          return of({ success: false, error: 'System failure' });
        } else {
          return of({ success: false, error: 'Uncaught' });
        }
      })
    );
  }
}
